// Security tab: password change form with visibility toggles.
'use client';

import { Eye, EyeOff } from 'lucide-react';
import { FIELD_LABEL_CLASS } from '@auctogon/ui/web';
import type { PasswordFields, PasswordVisibility } from './profile-modal-helpers';
import Alert from '../../ui/Alert';

/** Props for the security/password-change tab. */
export interface SecurityTabContentProps {
  passwords: PasswordFields;
  showPasswords: PasswordVisibility;
  isSaving: boolean;
  onPasswordsChange: (updated: PasswordFields) => void;
  onShowPasswordsChange: (updated: PasswordVisibility) => void;
  onChangePassword: () => void;
}

/**
 * Renders the password change form with current/new/confirm fields and submit button.
 * @param props - Password state, visibility toggles, and action handlers
 */
export default function SecurityTabContent({
  passwords,
  showPasswords,
  isSaving,
  onPasswordsChange,
  onShowPasswordsChange,
  onChangePassword,
}: SecurityTabContentProps) {
  return (
    <div className="space-y-6">
      <Alert kind="warning" variant="classic" role="note">
        For security, you&apos;ll need to enter your current password to make changes.
      </Alert>

      {/* Current password */}
      <PasswordField
        id="currentPassword"
        label="Current password"
        value={passwords.current}
        visible={showPasswords.current}
        onChange={(val) => onPasswordsChange({ ...passwords, current: val })}
        onToggleVisibility={() => onShowPasswordsChange({ ...showPasswords, current: !showPasswords.current })}
      />

      {/* New password */}
      <PasswordField
        id="newPassword"
        label="New password"
        value={passwords.new}
        visible={showPasswords.new}
        hint="Must be at least 8 characters"
        onChange={(val) => onPasswordsChange({ ...passwords, new: val })}
        onToggleVisibility={() => onShowPasswordsChange({ ...showPasswords, new: !showPasswords.new })}
      />

      {/* Confirm Password */}
      <PasswordField
        id="confirmPassword"
        label="Confirm new password"
        value={passwords.confirm}
        visible={showPasswords.confirm}
        onChange={(val) => onPasswordsChange({ ...passwords, confirm: val })}
        onToggleVisibility={() => onShowPasswordsChange({ ...showPasswords, confirm: !showPasswords.confirm })}
      />

      <button
        type="button"
        onClick={onChangePassword}
        disabled={!passwords.current || !passwords.new || !passwords.confirm || isSaving}
        className="w-full px-4 py-2.5 bg-primary hover:bg-primary-hover disabled:bg-gray-300 disabled:cursor-not-allowed text-white disabled:text-gray-500 rounded-lg transition-colors font-medium"
      >
        {isSaving ? 'Updating...' : 'Update password'}
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------ */

interface PasswordFieldProps {
  id: string;
  label: string;
  value: string;
  visible: boolean;
  hint?: string;
  onChange: (value: string) => void;
  onToggleVisibility: () => void;
}

/** A single password input with a show/hide toggle button. */
function PasswordField({ id, label, value, visible, hint, onChange, onToggleVisibility }: PasswordFieldProps) {
  return (
    <div>
      <label htmlFor={id} className={FIELD_LABEL_CLASS}>
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2.5 pr-10 text-primary focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent"
        />
        <button
          type="button"
          onClick={onToggleVisibility}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
        >
          {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
      {hint && <p className="mt-1 text-caption text-gray-500">{hint}</p>}
    </div>
  );
}
